import { findIndex, findLastIndex, trimEnd } from 'lodash';
import { FormatRule } from '../types';

export const MAX_FRACTIONAL_DIGITS = 20;

export function getPointIndex(value: number | string) {
  // 2.2 => 1
  // 213.123412 => 3

  return findLastIndex(
    typeof value === 'string' ? value : value.toFixed(MAX_FRACTIONAL_DIGITS),
    c => !isFinite(+c)
  );
}

// Only for numbers with decimal part, because it formats all ending zeros (means 1000 can become 10)
export function formatZeros(formattedValue: string, options?: { minZerosLength?: number }) {
  const trimmed = trimEnd(formattedValue, '0');
  const pointPosition = trimmed.length - getPointIndex(trimmed);

  if (pointPosition === 1) {
    if (options?.minZerosLength && options.minZerosLength !== 0) {
      // 123.0000 => 123. => 123.00 (if minZerosLength === 2)
      return trimmed.padEnd(trimmed.length + options.minZerosLength, '0');
    }
    // 123.0000 => 123. => 123
    return trimmed.slice(0, -1);
  } else if (pointPosition % 2 === 0) {
    // 123.1210 => 123.121 => 123.1210
    return trimmed.padEnd(trimmed.length + 1, '0');
  }
  // 123.1200 => 123.12
  return trimmed;
}

function findFirstNoZeroDigitPosition(value: number) {
  // Means any digit except zero
  // 0.134634 => 1
  // 0.00334634 => 3
  // 123.000043346 => 5

  const pointIndex = getPointIndex(value);
  const decimalPart = value.toFixed(MAX_FRACTIONAL_DIGITS).substring(pointIndex + 1);

  return findIndex(decimalPart, digit => digit !== '0') + 1;
}

export function formatNumber(value: number, rule: FormatRule, locale?: string) {
  const module = Math.abs(value);

  let decimalLength: number;

  switch (rule) {
    case 'as is': {
      decimalLength = MAX_FRACTIONAL_DIGITS;
      break;
    }
    case '0': {
      decimalLength = 0;
      break;
    }
    case '1': {
      decimalLength = 1;
      break;
    }
    case '2': {
      decimalLength = 2;
      break;
    }
    case '0-2': {
      decimalLength = module > 100 ? 0 : 2;
      break;
    }
    case '0-2-4-6': {
      if (module === 0) {
        decimalLength = 0;
      } else if (module < 0.000001) {
        decimalLength = findFirstNoZeroDigitPosition(module);
      } else if (module < 0.0001) {
        decimalLength = 6;
      } else if (module < 1) {
        decimalLength = 4;
      } else if (module < 100) {
        decimalLength = 2;
      } else {
        decimalLength = 0;
      }

      break;
    }
    case '2-4-6': {
      if (module === 0) {
        decimalLength = 2;
      } else if (module < 0.000001) {
        decimalLength = findFirstNoZeroDigitPosition(module);
      } else if (module < 0.0001) {
        decimalLength = 6;
      } else if (module < 1) {
        decimalLength = 4;
      } else {
        decimalLength = 2;
      }

      break;
    }
    case '4-6': {
      if (module === 0) {
        decimalLength = 4;
      } else if (module < 0.000001) {
        decimalLength = findFirstNoZeroDigitPosition(module);
      } else if (module < 0.0001) {
        decimalLength = 6;
      } else {
        decimalLength = 4;
      }

      break;
    }
    default:
      throw new Error(`Unknown truncate type = ${rule}`);
  }

  const formattedNumber = value.toLocaleString(locale, {
    maximumFractionDigits: decimalLength,
    minimumFractionDigits: rule === 'as is' ? 0 : decimalLength
  });

  // Rules that don't require formatting decimal zeros
  if (rule === '0' || rule === '1' || rule === '2' || rule === 'as is' || decimalLength === 0) {
    return { formattedNumber, decimalLength };
  }

  if (rule === '0-2' || rule === '0-2-4-6') {
    return { formattedNumber: formatZeros(formattedNumber), decimalLength };
  }

  return { formattedNumber: formatZeros(formattedNumber, { minZerosLength: 2 }), decimalLength };
}
